import Vue from "vue";
import VueRouter from "vue-router";

import store from "../store";
import AddEditPromotion from "../views/AddEditPromotion.vue";
import AddEditUser from "../views/AddEditUser.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import Home from "../views/Home.vue";
import Login from "../views/Login.vue";
import PasswordReset from "../views/PasswordReset.vue";
import PromotionManagement from "../views/PromotionManagement.vue";
import UserManagement from "../views/UserManagement.vue";
import IRouteConfig from "./IRouteConfig";
import IRouteConfigMeta from "./IRouteConfigMeta";
import OrderHistory from "../views/OrderHistory.vue";

Vue.use(VueRouter);

const HomeRoute: IRouteConfig = {
    path: "/",
    name: "home",
    component: Home,
    meta: {
        title: "Home",
        role: () => store.state.roleValues?.dashboardReportOnly
    }
};

const LoginRoute: IRouteConfig = {
    path: "/login",
    name: "login",
    component: Login,
    meta: {
        allowAnonymous: true,
        title: "Login",
        breadcrumbs: [HomeRoute]
    },
    props: route => ({
        returnUrl: route.query.returnUrl,
        passwordReset: route.query.passwordReset === true.toString(),
        title: route.meta.title
    })
};

export const ForgotPasswordRoute: IRouteConfig = {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword,
    props: route => ({ title: route.meta.title }),
    meta: {
        allowAnonymous: true,
        title: "Forgot Password",
        breadcrumbs: [HomeRoute]
    }
};

const PasswordResetRoute: IRouteConfig = {
    path: "/password-reset/:id",
    name: "password-reset",
    component: PasswordReset,
    meta: {
        allowAnonymous: true,
        title: "Password Reset",
        breadcrumbs: [HomeRoute]
    },
    props: route => ({
        userId: Number(route.params.id),
        token: route.query.token,
        title: route.meta.title
    })
};

const PromotionManagementRoute: IRouteConfig = {
    path: "/promotions",
    name: "promotions",
    component: PromotionManagement,
    meta: {
        title: "Promotion Management",
        breadcrumbs: [HomeRoute],
        role: () => store.state.roleValues?.administrator
    },
    props: route => ({ title: route.meta.title })
};

const UserRoute: IRouteConfig = {
    path: "/users",
    name: "users",
    component: UserManagement,
    meta: {
        title: "User Management",
        breadcrumbs: [HomeRoute],
        role: () => store.state.roleValues?.administrator
    },
    props: route => ({ title: route.meta.title })
};

const EditPromotionRoute: IRouteConfig = {
    path: "/promotion/edit/:id(\\d+)",
    name: "promotion/edit",
    component: AddEditPromotion,
    meta: {
        title: "Edit Promotion",
        breadcrumbs: [HomeRoute, PromotionManagementRoute],
        role: () => store.state.roleValues?.administrator
    },
    props: route => ({
        id: new Number(route.params.id),
        title: route.meta.title,
        syncOnLoad: route.query.sync === "true"
    })
};

const EditUserRoute: IRouteConfig = {
    path: "/user/edit/:id(\\d+)",
    name: "user/edit",
    component: AddEditUser,
    meta: {
        title: "Edit User",
        breadcrumbs: [HomeRoute, UserRoute],
        role: () => store.state.roleValues?.administrator
    },
    props: route => ({
        id: route.params.id ? parseInt(route.params.id) : null,
        title: route.meta.title
    })
};

const AddPromotionRoute: IRouteConfig = {
    path: "/promotion/create",
    name: "promotion/create",
    component: AddEditPromotion,
    meta: {
        title: "Add Promotion",
        breadcrumbs: [HomeRoute, PromotionManagementRoute],
        role: () => store.state.roleValues?.administrator
    },
    props: route => ({ title: route.meta.title })
};

const AddUserRoute: IRouteConfig = {
    path: "/user/create",
    name: "user/create",
    component: AddEditUser,
    meta: {
        title: "Add User",
        breadcrumbs: [HomeRoute, UserRoute],
        role: () => store.state.roleValues?.administrator
    },
    props: route => ({ title: route.meta.title })
};

const orderHistoryRoute: IRouteConfig = {
    path: "/order-history",
    name: "order-history",
    component: OrderHistory,
    meta: {
        title: "Order History",
        breadcrumbs: [HomeRoute],
        role: () => store.state.roleValues?.administrator
    }
};

const routes = [
    HomeRoute,
    LoginRoute,
    ForgotPasswordRoute,
    PasswordResetRoute,
    PromotionManagementRoute,
    UserRoute,
    EditPromotionRoute,
    AddPromotionRoute,
    EditUserRoute,
    AddUserRoute,
    orderHistoryRoute
];

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes
});

// redirect to login if the route requires authentication
router.beforeEach((to, from, next) => {
    if (
        to.meta.allowAnonymous !== true &&
        store.getters.isAuthenticated !== true
    ) {
        next({ name: LoginRoute.name, query: { returnUrl: to.fullPath } });
        return;
    }

    if (
        to.meta.role &&
        store.state.user?.role !== to.meta.role() &&
        to.name !== "home"
    ) {
        next({ name: "home" });
        return;
    }

    next();
});

// set the document title based on the meta title
router.afterEach((to, from) => {
    if (to.meta.title) {
        document.title = to.meta.title;
    } else {
        document.title = "Promotion Management";
    }
});

export default router;

export {
    HomeRoute,
    LoginRoute,
    PromotionManagementRoute,
    UserRoute,
    EditPromotionRoute,
    AddPromotionRoute,
    PasswordResetRoute,
    IRouteConfig,
    IRouteConfigMeta,
    EditUserRoute,
    AddUserRoute
};
